import { Box, TextField, Autocomplete, Skeleton, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { colors } from '@src/styles';
import { SelectOrganizationStepProps } from './types';
import { dropdownPaperSX, filterFieldSX, monoFontFamily, noticeSX, skeletonSX } from '../../scriptsStyles';

export const SelectOrganizationStep = ({
  organizations,
  selectedOrganization,
  onOrganizationChange,
  isLoadingOrganizations,
}: SelectOrganizationStepProps) => {
  if (isLoadingOrganizations) {
    return (
      <Box aria-busy="true">
        <Skeleton variant="text" width={320} height={20} sx={{ ...skeletonSX, mb: 1.5 }} />
        <Skeleton variant="rounded" height={44} sx={{ ...skeletonSX, borderRadius: '8px' }} />
      </Box>
    );
  }

  const organizationOptions = Object.entries(organizations)
    .map(([id, name]) => ({ value: id, label: name }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' }));

  const selectedOption = organizationOptions.find((org) => org.value === selectedOrganization) || null;

  return (
    <Box>
      <Typography sx={{ color: colors.brand.textSecondary, fontSize: '13.5px', mb: 1.5 }}>
        Selecione a organização que receberá o script canônico.
      </Typography>

      <Autocomplete
        value={selectedOption}
        onChange={(_event, newValue) => {
          onOrganizationChange(newValue?.value || '');
        }}
        options={organizationOptions}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        filterOptions={(options, { inputValue }) => {
          if (!inputValue) return options;
          const searchTerm = inputValue.toLowerCase();
          return options.filter(
            (option) =>
              option.label.toLowerCase().includes(searchTerm) || option.value.toLowerCase().includes(searchTerm),
          );
        }}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.value}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: '13px', color: colors.primary.white }}>
                {option.label}
              </Typography>
              <Typography noWrap sx={{ fontFamily: monoFontFamily, fontSize: '10.5px', color: colors.brand.textMuted }}>
                {option.value}
              </Typography>
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            placeholder="Digite o nome ou ID da organização"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <SearchIcon sx={{ fontSize: 18, color: `${colors.brand.textMuted} !important`, ml: 0.5 }} />
              ),
            }}
            sx={{
              ...filterFieldSX,
              '& .MuiOutlinedInput-root': {
                ...filterFieldSX['& .MuiOutlinedInput-root'],
                height: 44,
                fontSize: '14px',
                backgroundColor: colors.background.primary,
              },
            }}
          />
        )}
        slotProps={{
          paper: { sx: dropdownPaperSX },
          listbox: { sx: { maxHeight: 300 } },
        }}
        noOptionsText="Nenhuma organização encontrada"
      />

      {organizationOptions.length === 0 && (
        <Box sx={{ ...noticeSX(colors.status.running), mt: 2 }}>
          <InfoOutlinedIcon />
          Nenhuma organização disponível.
        </Box>
      )}
    </Box>
  );
};
